import { ModeEditOutlined as ModeEditOutlinedIcon } from 'components/icons';
import HoursInput from 'components/forms/inputs/rhf-inputs/HoursInput';
import HoursDisplay, { formatToHours } from 'components/util/HoursDisplay';
import LabelledData from 'pages/timecards/LabelledData';
import { timecardSecondsToHours } from 'pages/timecards/utils/timecardUtils';
import React, { useCallback, useState } from 'react';
import { Control, Path, UseFormSetValue, useWatch } from 'react-hook-form';
import { InvoiceMatterForm, InvoiceTimecard } from 'types/invoice-details';
import { hoursToSeconds } from 'utils/time';
import { InvoiceDetailsParameterizedTracker } from '../../InvoiceDetailsChangeTrackers';
import styles from './BilledHoursDisplay.module.css';

type Props = {
    isRowDisabled: boolean;
    readonly: boolean;
    advancedBillingEnabled: boolean;
    index: number;
    control: Control<InvoiceMatterForm>;
    onSubmit: () => void;
    setValue: UseFormSetValue<InvoiceMatterForm>;
    invoiceTimecard: InvoiceTimecard;
    buttonsDisabled: boolean;
    isTwoDecimalDisplayEnabled: boolean;
    isTwoDecimalEntryEnabled: boolean;
    displayDecimalScale: 1 | 2;
    /** The figure alone, where a column header names it; this then names the control, for a screen reader. */
    editLabel?: string;
};

export const BilledHoursDisplay = React.memo(
    ({
        index,
        control,
        onSubmit,
        setValue,
        isRowDisabled,
        readonly,
        advancedBillingEnabled,
        invoiceTimecard,
        buttonsDisabled: areButtonsDisabled,
        isTwoDecimalDisplayEnabled,
        isTwoDecimalEntryEnabled,
        displayDecimalScale,
        editLabel,
    }: Props) => {
        const [isEditMode, setIsEditMode] = useState(false);
        const [isBilledHoursFieldHovered, setIsBilledHoursFieldHovered] = useState(false);
        const { timecard } = invoiceTimecard;

        const [billedTimeInSeconds, billedTimeInHours, isNonBillable] = useWatch({
            control,
            name: [
                `invoiceTimecards.${index}.billedTimeInSeconds`,
                `invoiceTimecards.${index}.billedTimeInHours`,
                `invoiceTimecards.${index}.isNonBillable`,
            ],
        });

        const onHoursChange = useCallback(
            (hoursValue: string | undefined, secondsKey: Path<InvoiceMatterForm>): void => {
                let secondsValue: number | null = 0;
                if (hoursValue && hoursValue.length > 0) {
                    // eslint-disable-next-line no-useless-escape
                    const hours = parseFloat(hoursValue.split(/[,\.]/).filter(Boolean).join('.'));
                    secondsValue = hoursToSeconds(hours);
                }
                setValue(secondsKey, secondsValue);
            },
            [setValue]
        );

        // The hours string carries entry precision (and is zeroed by a non-billable intent while the seconds are
        // kept for restoring), so the read-only value comes from the seconds at display precision unless nothing is
        // billed.
        const billedFigure = (
            <span className="font-semibold">
                <HoursDisplay
                    value={
                        Number(billedTimeInHours) === 0
                            ? 0
                            : timecardSecondsToHours(billedTimeInSeconds ?? 0, isTwoDecimalDisplayEnabled)
                    }
                />
            </span>
        );

        return (
            <div>
                {!isEditMode ? (
                    <div
                        className={`${styles.displayRow} ${!readonly && !isRowDisabled ? styles.displayRowClickable : ''} ${readonly || isRowDisabled ? styles.displayRowDisabled : ''}`}
                        role="button"
                        tabIndex={readonly || isRowDisabled ? -1 : 0}
                        onMouseEnter={() => setIsBilledHoursFieldHovered(true)}
                        onMouseLeave={() => setIsBilledHoursFieldHovered(false)}
                        onClick={() => {
                            setIsEditMode(true);
                            setIsBilledHoursFieldHovered(false);
                        }}
                        onKeyDown={(event) => {
                            if (readonly || isRowDisabled) return;
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                setIsEditMode(true);
                                setIsBilledHoursFieldHovered(false);
                            }
                        }}
                    >
                        {editLabel ? (
                            <>
                                <span className="sr-only">{editLabel}</span>
                                {billedFigure}
                            </>
                        ) : (
                            <LabelledData
                                label={
                                    advancedBillingEnabled
                                        ? 'invoices.details.table.billed'
                                        : 'invoices.details.table.time-billed'
                                }
                                inline
                            >
                                {billedFigure}
                            </LabelledData>
                        )}
                        {isBilledHoursFieldHovered ? (
                            <ModeEditOutlinedIcon className="text-grey-500" fontSize="small" />
                        ) : null}
                    </div>
                ) : (
                    <HoursInput
                        gap={advancedBillingEnabled ? 1 : 0}
                        wrapperClassName="max-w-[100px]"
                        control={control}
                        name={`invoiceTimecards.${index}.billedTimeInHours`}
                        label={'invoices.details.table.billed'}
                        onBlur={() => {
                            if (billedTimeInHours && billedTimeInSeconds !== invoiceTimecard.billedTimeInSeconds) {
                                onSubmit();
                            } else if (!billedTimeInHours) {
                                setValue(
                                    `invoiceTimecards.${index}.billedTimeInSeconds`,
                                    invoiceTimecard.billedTimeInSeconds
                                );
                                setValue(
                                    `invoiceTimecards.${index}.billedTimeInHours`,
                                    String(
                                        timecardSecondsToHours(
                                            invoiceTimecard.billedTimeInSeconds,
                                            isTwoDecimalEntryEnabled
                                        )
                                    )
                                );
                            }
                            setIsEditMode(false);
                        }}
                        onChange={(event) =>
                            onHoursChange(event.target.value, `invoiceTimecards.${index}.billedTimeInSeconds`)
                        }
                        readonly={readonly}
                        disabled={isNonBillable || areButtonsDisabled}
                        autoFocus
                    />
                )}
                <InvoiceDetailsParameterizedTracker
                    currentParam={billedTimeInSeconds || 0}
                    displayValue={formatToHours(
                        timecardSecondsToHours(timecard.billedTimeInSeconds || 0, isTwoDecimalDisplayEnabled),
                        displayDecimalScale
                    )}
                    storedParam={timecard.billedTimeInSeconds}
                    textWrap="nowrap"
                />
            </div>
        );
    }
);
